import { getMetrikaCounterIdFromEnv } from "./metrikaCounterId";
import { buildMetrikaPurchase } from "./metrikaPurchase";
import { claimEcommerceSendForPayment, PENDING_PAYMENT_STORAGE_KEY, } from "./paymentAnalytics";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
/**
 * Цели Яндекс.Метрики — ЕДИНСТВЕННОЕ место, где живут их идентификаторы.
 *
 * ⚠️ ВЛАДЕЛЬЦУ: Метрика не создаёт цели сама. Каждое имя ниже нужно завести в
 * кабинете счётчика (Настройка → Цели → «JavaScript-событие», идентификатор —
 * ровно эта строка). Пока цели нет, reachGoal уходит в счётчик, но нигде не
 * считается — воронка будет пустой.
 *
 * Воронка сайта (по образцу VPN, но со своим продуктовым шагом «песня»):
 *   cta_click → song_gen_start → checkout_open → checkout_submit →
 *   payment_success
 *
 * Имена намеренно совпадают с событиями СОБСТВЕННОЙ аналитики
 * (`analyticsEvents.ts`) — это две РАЗНЫЕ системы (Метрика и своя БД), но общий
 * словарь позволяет сверять цифры между ними один-в-один.
 */
/**
 * Клик по CTA «сделать песню» на лендинге. Параметр `placement` — откуда
 * кликнули (шапка, hero, плавающая кнопка…), как `placement` у VPN.
 */
export const YM_GOAL_CTA_CLICK = "cta_click";
/**
 * Нажата «Создать песню» — продуктовый шаг, которого нет в воронке VPN, но
 * который в Песнях и есть главное намерение (именно тут упирается в баланс и
 * появляется пейвол).
 */
export const YM_GOAL_SONG_GEN_START = "song_gen_start";
/** Открыт экран оплаты: пейвол, страница баланса или модалка пополнения. */
export const YM_GOAL_CHECKOUT_OPEN = "checkout_open";
/** Платёж создан, уводим пользователя на кассу (получен payment_url). */
export const YM_GOAL_CHECKOUT_SUBMIT = "checkout_submit";
/**
 * Оплата подтверждена — нижняя ступень воронки, шлётся из браузера вместе с
 * ecommerce-покупкой.
 *
 * НЕ переиспользуем `YM_GOAL_PAYMENT` намеренно: ту же цель шлёт БЭКЕНД через
 * Measurement Protocol (см. `track_payment`, ENV
 * `WEB_YANDEX_METRIKA_PURCHASE_GOAL_ID`), и на веб-пользователе с известным
 * ycid обе отправки попали бы на один и тот же ClientID — достижения цели
 * задвоились бы. Разные имена дают честные цифры и позволяют сравнить
 * браузерный и серверный каналы.
 */
export const YM_GOAL_PAYMENT_SUCCESS = "payment_success";
/**
 * Цель, которую шлёт БЭКЕНД (Measurement Protocol / офлайн-конверсии). Держим
 * здесь как документацию имени: оно должно совпадать с ENV
 * `WEB_YANDEX_METRIKA_PURCHASE_GOAL_ID`. Из браузера её НЕ шлём — см.
 * `YM_GOAL_PAYMENT_SUCCESS`.
 */
export const YM_GOAL_PAYMENT = "payment_goal";
// Тег Метрики грузится асинхронно (см. yandexMetrikaTagLoader), поэтому ранняя
// цель — например `checkout_open` при ПРЯМОМ заходе на пейвол — легко
// опережает момент, когда `window.ym` определён, и молча теряется. Ждём
// готовности короткими опросами с жёстким дедлайном.
const YM_READY_TIMEOUT_MS = 2000;
const YM_READY_POLL_MS = 50;
/**
 * Зовёт `fire`, как только доступен `window.ym`. Быстрый путь — тег уже
 * поднялся (обычный случай для кликов); иначе опрашиваем `window.ym` раз в
 * YM_READY_POLL_MS до дедлайна YM_READY_TIMEOUT_MS и срабатываем ровно один
 * раз: что первым, появление ym или таймаут (флаг `settled`). По таймауту тихо
 * сдаёмся — трекинг best-effort и не должен ничего ломать.
 */
function whenYmReady(fire) {
    if (typeof window === "undefined") {
        return;
    }
    const ymNow = window.ym;
    if (typeof ymNow === "function") {
        fire(ymNow);
        return;
    }
    let settled = false;
    const poll = window.setInterval(() => {
        if (settled) {
            return;
        }
        const ym = window.ym;
        if (typeof ym === "function") {
            settled = true;
            window.clearInterval(poll);
            window.clearTimeout(deadline);
            fire(ym);
        }
    }, YM_READY_POLL_MS);
    const deadline = window.setTimeout(() => {
        if (settled) {
            return;
        }
        settled = true;
        window.clearInterval(poll);
    }, YM_READY_TIMEOUT_MS);
}
function callYm(method, ...args) {
    if (typeof window === "undefined") {
        return;
    }
    const id = getMetrikaCounterIdFromEnv();
    if (id === null) {
        return;
    }
    whenYmReady((ym) => {
        try {
            ym(id, method, ...args);
        }
        catch {
            /* синхронное исключение из ym(...) — best-effort, молчим */
        }
    });
}
/**
 * Виртуальный хит для SPA: корректные URL в отчётах Метрики.
 */
export function ymHitPage(urlPath, title) {
    const path = urlPath.startsWith("/") ? urlPath : `/${urlPath}`;
    callYm("hit", path, {
        title: title || (typeof document !== "undefined" ? document.title : ""),
    });
}
/**
 * Цель Метрики. Best-effort: ждёт готовности тега (см. whenYmReady), глушит
 * исключения и НИКОГДА не бросает наружу — трекинг не должен ломать ни клик,
 * ни навигацию.
 */
export function ymReachGoal(goalId, params) {
    if (params && Object.keys(params).length > 0) {
        callYm("reachGoal", goalId, params);
    }
    else {
        callYm("reachGoal", goalId);
    }
}
// Цели, отправленные за эту загрузку страницы (см. ymReachGoalOnce). Переменная
// уровня модуля, а не storage: воронка Метрики считает ВИЗИТЫ, поэтому «один
// раз за загрузку» — правильная гранулярность, и на новой загрузке цель обязана
// уйти снова.
const goalsSentOnce = new Set();
/**
 * Цель, отправляемая не чаще одного раза за загрузку страницы.
 *
 * Нужна ступеням, которые в SPA легко продублировать: `checkout_open` иначе
 * уходил бы на каждый ререндер эффекта, на открытие модалки пополнения поверх
 * уже открытого пейвола и на возврат по «назад».
 */
export function ymReachGoalOnce(goalId, params) {
    if (goalsSentOnce.has(goalId)) {
        return;
    }
    goalsSentOnce.add(goalId);
    ymReachGoal(goalId, params);
}
// Дедуп setUserID — на ЗАГРУЗКУ СТРАНИЦЫ, а не на браузер: Метрика связывает
// собственный идентификатор с ClientID только в тех визитах, во время которых
// setUserID был вызван, поэтому маркер в storage заглушил бы связку во всех
// последующих визитах. Переменная модуля переживает StrictMode и повторные
// вызовы /me за одну загрузку, но сбрасывается на полной перезагрузке. Храним
// само значение, а не флаг: смена аккаунта в той же вкладке (logout → вход
// другим) отправит связку заново.
let lastYmUserIdSent = null;
/**
 * Привязка нашего id пользователя к его ClientID (`setUserID`).
 *
 * Нужна офлайн-конверсиям Метрики: бэкенд грузит оплаты по колонке `UserId` со
 * значением `str(user.id)`, и Метрика сматчит их только с теми визиторами, в
 * чьих визитах сайт вызывал setUserID.
 */
export function setYmUserId(userId) {
    const value = String(userId).trim();
    if (!value) {
        return;
    }
    if (value === lastYmUserIdSent) {
        return;
    }
    // Помечаем отправленным сразу: иначе повторные вызовы /me за ту же загрузку
    // наплодили бы параллельных ожидающих опросов whenYmReady.
    lastYmUserIdSent = value;
    callYm("setUserID", value);
}
/**
 * Электронная торговля Метрики (контейнер dataLayer).
 * @see https://yandex.ru/support/metrica/ru/ecommerce/data
 */
export function pushMetrikaPurchase(payload) {
    if (typeof window === "undefined") {
        return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
        ecommerce: {
            currencyCode: payload.currencyCode || "RUB",
            purchase: {
                actionField: {
                    id: payload.transactionId,
                    revenue: payload.revenue,
                },
                products: payload.products.map((p) => ({
                    id: p.id,
                    name: p.name,
                    price: p.price,
                    quantity: p.quantity,
                    ...(p.category ? { category: p.category } : {}),
                })),
            },
        },
    });
}
export function rememberPendingPaymentForAnalytics(paymentDbId) {
    const v = String(paymentDbId);
    try {
        sessionStorage.setItem(PENDING_PAYMENT_STORAGE_KEY, v);
    }
    catch {
        /* ignore */
    }
    try {
        localStorage.setItem(PENDING_PAYMENT_STORAGE_KEY, v);
    }
    catch {
        /* ignore */
    }
}
export function readPendingPaymentIdFromStorage() {
    let raw = null;
    try {
        raw = sessionStorage.getItem(PENDING_PAYMENT_STORAGE_KEY);
    }
    catch {
        /* ignore */
    }
    if (!raw) {
        try {
            raw = localStorage.getItem(PENDING_PAYMENT_STORAGE_KEY);
        }
        catch {
            /* ignore */
        }
    }
    if (!raw || !/^\d+$/.test(raw)) {
        return null;
    }
    return Number(raw);
}
export function clearPendingPaymentFromStorage() {
    try {
        sessionStorage.removeItem(PENDING_PAYMENT_STORAGE_KEY);
    }
    catch {
        /* ignore */
    }
    try {
        localStorage.removeItem(PENDING_PAYMENT_STORAGE_KEY);
    }
    catch {
        /* ignore */
    }
}
/**
 * Успешная оплата в Метрику: цель `payment_success` + ecommerce-покупка. Ровно
 * один раз на платёж.
 *
 * Идемпотентность обязана быть своей: у Метрики нет дедупликации ни между
 * загрузками страницы, ни между вкладками, а возврат с кассы приходит сразу по
 * двум путям — вкладка `/payment/success` и сигнал в остальные вкладки
 * приложения (см. paymentReturn). Право на отправку занимаем ОДНИМ синхронным
 * вызовом claimEcommerceSendForPayment (маркер общий на все вкладки).
 */
export function reportMetrikaPurchaseOnce(payment) {
    if (!claimEcommerceSendForPayment(payment.id)) {
        return;
    }
    const purchase = buildMetrikaPurchase(payment);
    ymReachGoal(YM_GOAL_PAYMENT_SUCCESS, {
        payment_id: payment.id,
        amount_rub: purchase.revenue,
        ...(payment.package_id != null ? { package_id: payment.package_id } : {}),
    });
    pushMetrikaPurchase(purchase);
}
/**
 * После первого прогона (счётчик уже зафиксировал URL из document) шлём hit на смену маршрута.
 */
export function useYandexMetrikaPageHit() {
    const location = useLocation();
    const path = `${location.pathname}${location.search}`;
    const skipNextRef = useRef(true);
    useEffect(() => {
        if (skipNextRef.current) {
            skipNextRef.current = false;
            return;
        }
        ymHitPage(path);
    }, [path]);
}
